import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { Button, Tooltip } from "@mui/material";
import { orange } from "@mui/material/colors";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { CredentialOwnerChip, credentialOwnerLabel } from "components/CredentialOwnerChip";
import { CredentialVerificationStatusChip } from "components/CredentialVerificationStatusChip";
import { CredentialsForStaffQuery, CredentialVerificationStatus } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { credentialVerificationStatusToLabel } from "util/constants";
import { prettyPrintDateTimeMed } from "util/dates";

import { CREDENTIALS_FOR_STAFF_QUERY, STAFF_PORTAL_OPTIONS } from "./api";
import { CredentialDetailDialog } from "./CredentialDetailDialog";

type Row = CredentialsForStaffQuery["credentialsForStaff"]["items"][number];
type DialogState = { mode: "add" } | { mode: "edit"; id: string };

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];

// Not a column: Tenants shows a count, so its filter needs a key of its own.
// `SEMANTIC_EQUIVALENTS` canonicalises `tenant` to the `tenantId` the resolver
// answers to — the same pairing the EPCRA grid uses.
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  { key: "tenant", header: "Tenant", filterKeyType: "multipleTenant" },
];

// Columns become filter keys automatically and the resolver silently drops keys it
// doesn't answer, so a column without a server key renders a filter that does
// nothing. These three have none: both counts, and Last Verified has no date handler.
const EXCLUDE_FILTER_COLUMNS = ["lastVerificationCheckAt", "tenantCount", "facilityCount"];

const CREDENTIAL_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(CredentialVerificationStatus).map((status) => [
    status,
    credentialVerificationStatusToLabel(status),
  ])
);

// Values stay "true"/"false" on the wire; only the labels change, so the popover
// reads in the column's terms rather than as a bare Yes/No.
const OWNER_FILTER_VALUES = ["true", "false"];
const OWNER_FILTER_LABELS: Record<string, string> = {
  true: credentialOwnerLabel(true),
  false: credentialOwnerLabel(false),
};

// `field` doubles as the server-side sort key, hence names like `isEncampOwned`
// rather than an "owner" field the resolver couldn't sort on.
const buildColumns = (portalNames: string[]): OmnisearchGridColDef<Row>[] => [
  {
    field: "name",
    headerName: "Credential name",
    flex: 1,
    // `name` is nullable, and a null one rendered as a blank cell with nothing to
    // identify the row by.
    renderCell: ({ row }) => row.name || "—",
  },
  {
    field: "username",
    headerName: "Username",
    flex: 1,
  },
  {
    field: "isEncampOwned",
    headerName: "Owner",
    flex: 0.45,
    filterKeyType: "enum",
    enumValues: OWNER_FILTER_VALUES,
    enumPresentationFunction: (value) => OWNER_FILTER_LABELS[value] ?? value,
    // valueGetter drives sort/export/copy, renderCell only the visuals.
    valueGetter: (_, row) => credentialOwnerLabel(row.isEncampOwned),
    renderCell: ({ row }) => <CredentialOwnerChip isEncampOwned={row.isEncampOwned} />,
  },
  {
    field: "portalName",
    headerName: "Portal",
    flex: 0.5,
    sortable: false,
    // A combo box over the portals that exist, so nobody guesses the spelling. The
    // values are the same `portalTech ?? jurisdiction` names the column renders.
    filterKeyType: "enumAutocomplete",
    enumValues: portalNames,
    // Verbatim: these are real portal names, not enum constants, and the default
    // pretty-printer title-cases them into things like "Nyc".
    enumPresentationFunction: (value) => value,
  },
  {
    field: "verificationStatus",
    headerName: "Credential Status",
    flex: 0.5,
    filterKeyType: "enum",
    enumValues: Object.values(CredentialVerificationStatus),
    enumPresentationFunction: (value) => CREDENTIAL_STATUS_LABELS[value] ?? value,
    renderCell: ({ row }) => <CredentialVerificationStatusChip status={row.verificationStatus} />,
  },
  {
    field: "lastVerificationCheckAt",
    headerName: "Last Verified",
    flex: 0.5,
    renderCell: ({ row }) =>
      row.lastVerificationCheckAt ? prettyPrintDateTimeMed(row.lastVerificationCheckAt) : "—",
  },
  {
    field: "currentlyInUse",
    headerName: "Currently in Use",
    flex: 0.35,
    // Derived from live Lock rows — nothing to order on, but filterable.
    sortable: false,
    filterKeyType: "boolean",
    renderCell: ({ row }) =>
      row.currentlyInUse ? (
        <Tooltip title="A credential lock is currently held for this login">
          <LockOutlined sx={{ color: orange[700], fontSize: 20 }} />
        </Tooltip>
      ) : null,
  },
  {
    field: "tenantCount",
    headerName: "Tenants",
    flex: 0.25,
    // Distinct-count from a separate aggregate query — nothing to order on.
    sortable: false,
  },
  {
    field: "facilityCount",
    headerName: "Facilities",
    flex: 0.25,
  },
];

const getItems = (data: CredentialsForStaffQuery) => data.credentialsForStaff.items;
const getCount = (data: CredentialsForStaffQuery) => data.credentialsForStaff.count;

const buildVariables = (
  omnisearch: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
) => ({
  search: omnisearch,
  page: pagination.page,
  pageSize: pagination.pageSize,
  sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
});

export function CredentialManagement() {
  useBreadcrumb({ label: "Credential Management" });
  const [dialog, setDialog] = useState<DialogState | null>(null);

  // Every portal a credential might sit on, retired ones included — the grid spans
  // them all. Deduped because several portals can share a tech name.
  const { data: portalData } = useQuery(STAFF_PORTAL_OPTIONS, {
    variables: { includeInactive: true },
  });
  const portalNames = useMemo(() => {
    const names = (portalData?.portalPickerOptions ?? []).map(
      (portal) => portal.portalTech ?? portal.jurisdiction
    );
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [portalData]);

  const columns = useMemo(() => buildColumns(portalNames), [portalNames]);

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => setDialog({ mode: "edit", id: params.row.id }),
    []
  );

  const handleClose = useCallback(() => setDialog(null), []);

  const handleAdd = useCallback(() => setDialog({ mode: "add" }), []);

  const handleOpenExisting = useCallback((id: string) => setDialog({ mode: "edit", id }), []);

  const commandButtons = useMemo(
    () => [
      <Button key="add-credentials" variant="contained" startIcon={<Add />} onClick={handleAdd}>
        Add credential
      </Button>,
    ],
    [handleAdd]
  );

  return (
    <>
      <OmnisearchDataGridV2<CredentialsForStaffQuery, ReturnType<typeof buildVariables>, Row>
        query={CREDENTIALS_FOR_STAFF_QUERY}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        columns={columns}
        commandButtons={commandButtons}
        persistenceKey="staff-credential-management"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        noRowsMessage="No credentials found."
        onRowClick={handleRowClick}
      />
      {/* Mounted only while open, and keyed per credential, so each open starts
          from a clean form. Kept mounted, the dialog carried its validation errors
          and values across closes — reopening Add showed the previous attempt's
          red fields, and its stale critical issues left the save button disabled. */}
      {dialog && (
        <CredentialDetailDialog
          key={dialog.mode === "edit" ? dialog.id : "add"}
          credentialId={dialog.mode === "edit" ? dialog.id : null}
          open
          onClose={handleClose}
          onOpenExisting={handleOpenExisting}
        />
      )}
    </>
  );
}
